import { onMounted, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import useStore from "../store.ts";
import { ensureStructuredCacheFlag } from "../cache/CacheControl.ts";
import { FeatureFlags } from "../utils/FeatureFlags.ts";
import { enableWorkers } from "../worker/WorkerInit.ts";
import { postDimensions, postLayout } from "../worker/RenderWorkerManager.ts";
import { hexToBytes } from "../utils/ByteUtils.ts";
import { getDeviceIdFromRouteQuery, getStoredDeviceId, storeDeviceId } from "../utils/DeviceId.ts";
import { useRouteBindings } from "./useRouteBindings.ts";

const requestOfflineLayout = async () => {
    try {
        const OFFLINE_FILE_LOCATION = import.meta.env.VITE_OFFLINE_FILE_LOCATION || "0.gpb";
        const response = await fetch(OFFLINE_FILE_LOCATION);
        const blob = await response.blob();
        let data = await blob.text();
        data = data.replace("0x", "");
        await useStore().socketManager.requestLayoutBuild({ "data": hexToBytes(data) });
    } catch (err) {
        console.error(err);
    }
};

/* see docs/EXTRACTED_COMMENTS.md#c075 */
export function useDisplayBoot(opts: { onResize: () => void }): void {
    const store = useStore();
    const router = useRouter();
    const route = useRoute();
    const { device } = useRouteBindings();

    watch(() => store.layout, (_next, previous) => {
        router.push({ path: `/${store.layout}`, query: { ...route.query } });
        const OFFLINE_PAGE = import.meta.env.VITE_OFFLINE_PAGE || "0";
        if (store.layout == OFFLINE_PAGE) {
            store.layoutData = store.homeData;
            postDimensions();
            postLayout(Object.keys(store.layoutData)[0].replace("_", ""));
        } else {
            // see docs/EXTRACTED_COMMENTS.md#c380
            store.layoutPending = true;
            // see docs/EXTRACTED_COMMENTS.md#c756
            if (previous == null) return;
            store.socketManager.requestLayout();
        }
    });

    onMounted(async () => {
        const deviceFromRoute = getDeviceIdFromRouteQuery(route.query as any);
        const effectiveDeviceId = deviceFromRoute ?? getStoredDeviceId();
        if (!effectiveDeviceId) {
            await router.push({ path: "/auth" });
            return;
        }
        // see docs/EXTRACTED_COMMENTS.md#c381
        device.value = effectiveDeviceId;
        if (!deviceFromRoute) {
            await router.replace({ path: route.path, query: { ...route.query, dev: effectiveDeviceId } });
        } else {
            storeDeviceId(deviceFromRoute);
        }
        if (route.params.id) {
            store.layout = Number(route.params.id);
        } else if (store.layout != null) {
            // see docs/EXTRACTED_COMMENTS.md#c382
            await router.replace({ path: `/${store.layout}`, query: { ...route.query, dev: effectiveDeviceId } });
        }
        // see docs/EXTRACTED_COMMENTS.md#c383
        store.socketManager.sendDevice(effectiveDeviceId);
        store.socketManager.connect();
        ensureStructuredCacheFlag(FeatureFlags.structuredCache);

        enableWorkers();
        try {
            await store.socketManager.getAuthenticationPromise();
        } catch {
            // see docs/EXTRACTED_COMMENTS.md#c384
        }
        await requestOfflineLayout();

        // Initial resize to account for status bar
        opts.onResize();
    });
}
